import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { agentPersonaIdSchema } from '../../common/agent.schema';
import { languageSchema } from '../../common/language.schema';
import { idSchema, nameSchema, objectIdSchema } from '../../validation';
import { conversationResponderModeSchema } from '../conversation/conversation.model';
import { channelAccountTypeSchema } from './channel-account.type.model';

/**
 * Transport provider behind the account (vendor), distinct from `type`.
 *
 * - `buraq`     — Buraq WhatsApp API (Hero / Wathiqa Plus lines until
 *                 they migrate). Sync inbound HTTP reply + CLOSE.
 * - `360dialog` — 360dialog Messaging Cloud (`waba-v2.360dialog.io`,
 *                 `D360-API-KEY` per number). Async webhook ACK.
 * - `meta`      — Meta Graph API directly (legacy template path).
 * - `resend`    — email channel (Phase 5).
 *
 * Read at runtime by the WhatsApp template / session transport to pick
 * how a message is put on the wire, so two Hero-owned lines can sit
 * behind different vendors. See ADR-022.
 */
export const channelAccountProviderSchema = z.enum([
  'buraq',
  '360dialog',
  'meta',
  'resend',
]);
export type ChannelAccountProvider = z.infer<typeof channelAccountProviderSchema>;

/**
 * How this WhatsApp number shares the green app.
 *
 * - `cloud_exclusive` — Cloud API owns the session (sandbox / dedicated
 *                       Cloud number). Default.
 * - `coexistence`     — business still uses WhatsApp Business App;
 *                       `smb_message_echoes` are reserved for a later
 *                       human-handoff router and are no-ops today.
 */
export const channelAccountOperatingModeSchema = z.enum([
  'cloud_exclusive',
  'coexistence',
]);
export type ChannelAccountOperatingMode = z.infer<
  typeof channelAccountOperatingModeSchema
>;

/**
 * Provider-side ids that are not the inbound routing key (`externalId`).
 * 360dialog: Hub `channel_id` / WABA id from Embedded Signup (later).
 */
export const channelAccountProviderMetaSchema = z.object({
  channelId: idSchema.optional(),
  wabaId: idSchema.optional(),
});
export type ChannelAccountProviderMeta = z.infer<
  typeof channelAccountProviderMetaSchema
>;

/**
 * Provisioning lifecycle of the account.
 *
 * - `pending`  — created but not yet verified with the provider
 *                (e.g. Meta embedded-signup in progress).
 * - `active`   — routable; inbound webhooks resolve to it and
 *                outbound sends are allowed.
 * - `disabled` — kept for history but no traffic (org disconnected
 *                the number, token revoked, etc.).
 */
export const channelAccountStatusSchema = z.enum(['pending', 'active', 'disabled']);
export type ChannelAccountStatus = z.infer<typeof channelAccountStatusSchema>;

/**
 * Per-account agent behavior. All optional — absent means platform
 * defaults (`hero_customer_support` persona, `responderMode: 'ai'`).
 *
 * Outbound routing is **not** a settings field: Buraq same-turn replies
 * still ride the inbound HTTP body; 360dialog Cloud posts after ACK
 * using this row's `credentialsRef` (env-var name, never the token).
 * Templates pick transport from `provider`. Branding is `personaId`.
 */
export const channelAccountSettingsSchema = z.object({
  /**
   * Persona that speaks on this account (e.g. a carrier-org account
   * may pin `carrier_dispatcher`, the Wathiqa Plus line pins
   * `wathiqa_plus`). Absent → runtime persona resolution
   * (`docs/ai/personas.md`).
   */
  personaId: agentPersonaIdSchema.optional(),
  /** Default responder mode stamped on new conversations. */
  responderMode: conversationResponderModeSchema.optional(),
  /** Org users pinged when the AI escalates a thread on this account. */
  escalationUserIds: z.array(objectIdSchema).optional(),
  /**
   * WhatsApp green-app coexistence. Absent → `cloud_exclusive`.
   * Product routing for echoes is near-future; the field is here so a
   * later `ChannelAccount` row can flip without a schema change.
   */
  operatingMode: channelAccountOperatingModeSchema.optional(),
  /**
   * Language staff read voice-note summaries in on this line. Used for
   * platform-owned lines (no `org`); an org line uses its
   * `Org.settings.ai_summary_language` first. Absent → `ar`.
   */
  aiSummaryLanguage: languageSchema.optional(),
});
export type ChannelAccountSettings = z.infer<typeof channelAccountSettingsSchema>;

/**
 * `ChannelAccount` — one connected communication endpoint (a WhatsApp
 * business number today; an email inbox or voice number later).
 *
 * The Hero platform number is just "account #1" (no `org` — platform
 * owned) — per-org accounts (Phase 4 "add your WhatsApp to Hero") are
 * more rows in the same collection, so webhook routing, conversation
 * stamping, and the inbox UI never special-case the platform number.
 *
 * `Conversation.channel.channelAccountId` points here when set.
 *
 * Indexes (see the `channel-account-collection` migration):
 * - `(type, externalId)` unique — webhook routing (Meta
 *   `phone_number_id` → account).
 * - `(org._id, type)` — "the org's connected channels".
 */
export const ChannelAccountSchema = createModel({
  /**
   * Owning org. **Absent → platform-owned** (the Hero number): Hero
   * the platform is not a network party (`partyRoleSchema` has no
   * platform role, by design — see `docs/relations-v2/02-data-model.md`),
   * so the platform account simply carries no org snapshot. Per-org
   * accounts (Phase 4 provisioning) always set it.
   */
  org: partySchema.optional(),

  type: channelAccountTypeSchema,
  provider: channelAccountProviderSchema,

  /**
   * Provider-side identifier — the routing key for inbound traffic.
   * Per type:
   * - `whatsapp` — Meta `phone_number_id`.
   * - `email`    — the inbox address.
   * - `voice`    — provider phone-number id.
   */
  externalId: idSchema.min(1),

  /** Human-readable identifier, e.g. `+9665…` or `support@org.com`. */
  displayId: nameSchema.optional(),

  /** Staff label, e.g. "Wathiqa Plus — main line". Admin-edited; routing never reads it. */
  title: nameSchema.optional(),
  /** Staff note: what this account is for, who answers it. Admin-edited. */
  description: z.string().max(1000).optional(),

  /**
   * Hero-owned system line (e.g. the platform WhatsApp number). Must be
   * seeded manually — runtime never auto-creates `ChannelAccount` rows.
   * Org-provisioned accounts leave this unset / `false`.
   */
  isSystemChannel: z.boolean().optional(),

  /**
   * Env-var **name** (or Secret Manager name mounted as env) for this
   * account's send key. Never store the token in Mongo.
   *
   * Required in practice for `provider: '360dialog'` — including the
   * system Hero row (`WHATSAPP_360DIALOG_API_KEY_HERO`). Leftover
   * `provider: 'buraq'` rows still parse; transport never sends them.
   */
  credentialsRef: idSchema.optional(),

  /** Extra provider ids (360dialog channel / WABA) — not the routing key. */
  providerMeta: channelAccountProviderMetaSchema.optional(),

  status: channelAccountStatusSchema,

  settings: channelAccountSettingsSchema.optional(),
});

export type ChannelAccount = z.infer<typeof ChannelAccountSchema.model>;
export type ChannelAccountData = z.infer<typeof ChannelAccountSchema.data>;
export type ChannelAccountPartial = z.infer<typeof ChannelAccountSchema.partial>;
export type ChannelAccountQueryOne = QueryOne<ChannelAccount>;
export type ChannelAccountQueryMany = QueryMany<ChannelAccount>;
